/* eslint-disable react/require-default-props */
import React, { useState, useEffect, useMemo } from 'react';
import toast from 'react-hot-toast';
import { translate } from '@utils/i18n';
import DetailSection from '@components/v2/DetailSection';
import KeyValueList, { IKeyValueType } from '@components/v2/KeyValueList';
import LoadableButton from '@components/LoadableButton/LoadableButton';
import ConfirmDialog from '@components/ConfirmDialog/ConfirmDialog';
import { namespace as proposalNamespace } from '../constants';
import './ProposalContactsSectionStyles.scss';
const namespace = `${proposalNamespace}.contacts`;
// Email validation regex - requires @ symbol and valid domain
const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
// Phone validation regex - allows international format with country code (+1234567890) or domestic (123-456-7890)
const PHONE_REGEX = /^(\+?1?[-.\s]?)?\(?([0-9]{3})\)?[-.\s]?([0-9]{3})[-.\s]?([0-9]{4})$|^\+[1-9]\d{1,14}$/;
const validateEmail = (email) => {
    if (!email.trim())
        return null; // Empty is allowed
    if (!EMAIL_REGEX.test(email.trim())) {
        return translate('invalid_email_format', { namespace });
    }
    return null;
};
const validatePhone = (phone) => {
    if (!phone.trim())
        return null; // Empty is allowed
    const cleanPhone = phone.replace(/[\s.-]/g, ''); // Remove common separators for length check
    if (cleanPhone.length > 15) {
        return translate('phone_too_long', { namespace });
    }
    if (!PHONE_REGEX.test(phone.trim())) {
        return translate('invalid_phone_format', { namespace });
    }
    return null;
};
export default function ProposalContactsSection({ primaryContact, contacts, onUpdatePrimaryContact, onCreateContact, onUpdateContact, onDeleteContact, isCreating = false, isDeleting = false }) {
    const [isAddingContact, setIsAddingContact] = useState(false);
    const [newContact, setNewContact] = useState({ name: '', email: '', phone: '', title: '' });
    const [contactToDelete, setContactToDelete] = useState(null);
    useEffect(() => {
        if (!isCreating) {
            setIsAddingContact(false);
            setNewContact({ name: '', email: '', phone: '', title: '' });
        }
    }, [isCreating, contacts]);
    const newContactFormDisabled = useMemo(() => {
        return Boolean((!newContact.name.trim() && !newContact.email.trim() && !newContact.phone.trim() && !newContact.title.trim()) ||
            validateEmail(newContact.email) ||
            validatePhone(newContact.phone));
    }, [newContact]);
    const handleAddContact = () => {
        setIsAddingContact(true);
    };
    const handleCancelAdd = () => {
        setIsAddingContact(false);
        setNewContact({ name: '', email: '', phone: '', title: '' });
    };
    const handleSaveNewContact = () => {
        const errors = {};
        // Validate email if provided
        const emailError = validateEmail(newContact.email);
        if (emailError) {
            errors['new-contact-email'] = emailError;
        }
        // Validate phone if provided
        const phoneError = validatePhone(newContact.phone);
        if (phoneError) {
            errors['new-contact-phone'] = phoneError;
        }
        if (Object.keys(errors).length > 0) {
            toast.error('Please correct the errors in the form before saving.');
            return;
        }
        // Only save if no validation errors and at least one field is filled
        if (Object.keys(errors).length === 0) {
            if (newContact.name.trim() || newContact.email.trim() || newContact.phone.trim() || newContact.title.trim()) {
                onCreateContact(newContact);
            }
        }
    };
    const handleDeleteContact = (contactId) => {
        setContactToDelete(contactId);
    };
    const confirmDelete = () => {
        if (contactToDelete) {
            onDeleteContact(contactToDelete);
            setContactToDelete(null);
        }
    };
    const cancelDelete = () => {
        setContactToDelete(null);
    };
    const formatContactKeyValueItems = (contact, contactId, isEditable = false) => [
        {
            key: translate('additional_contact', { namespace }),
            value: contact.name,
            type: IKeyValueType.text,
            editable: isEditable,
            field: contactId ? `contact-${contactId}-name` : 'new-contact-name'
        },
        {
            key: translate('job_title', { namespace }),
            value: contact.title,
            type: IKeyValueType.text,
            editable: true,
            field: contactId ? `contact-${contactId}-title` : 'new-contact-title'
        },
        {
            key: translate('email', { namespace }),
            value: contact.email,
            type: IKeyValueType.text,
            editable: isEditable,
            field: contactId ? `contact-${contactId}-email` : 'new-contact-email'
        },
        {
            key: translate('phone', { namespace }),
            value: contact.phone,
            type: IKeyValueType.phone,
            editable: isEditable,
            field: contactId ? `contact-${contactId}-phone` : 'new-contact-phone'
        }
    ];
    const primaryContactItems = [
        {
            key: translate('primary_contact', { namespace }),
            value: primaryContact.name,
            type: IKeyValueType.text,
            editable: true,
            field: 'preparedFor'
        },
        {
            key: translate('email', { namespace }),
            value: primaryContact.email,
            type: IKeyValueType.text,
            editable: true,
            field: 'preparedForEmail'
        },
        {
            key: translate('phone', { namespace }),
            value: primaryContact.phone,
            type: IKeyValueType.phone,
            editable: true,
            field: 'preparedForPhone'
        }
    ];
    const handleContactEdit = (field, value) => {
        if (field.startsWith('contact-')) {
            const [, contactIdStr, fieldName] = field.split('-');
            const contactId = parseInt(contactIdStr, 10);
            // Validate before updating
            if (fieldName === 'email') {
                const emailError = validateEmail(value);
                if (emailError) {
                    toast.error(emailError);
                    return;
                }
            }
            else if (fieldName === 'phone') {
                const phoneError = validatePhone(value);
                if (phoneError) {
                    toast.error(phoneError);
                    return;
                }
            }
            onUpdateContact(contactId, { [fieldName]: value });
        }
        else if (field.startsWith('new-contact-')) {
            const fieldName = field.replace('new-contact-', '');
            setNewContact((prev) => (Object.assign(Object.assign({}, prev), { [fieldName]: value })));
            // Validate new contact fields in real-time if errors are being shown
            if (fieldName === 'email') {
                const emailError = validateEmail(value);
                if (emailError) {
                    toast.error(emailError);
                }
            }
            else if (fieldName === 'phone') {
                const phoneError = validatePhone(value);
                if (phoneError) {
                    toast.error(phoneError);
                }
            }
        }
        else {
            // Validate primary contact fields
            if (field === 'preparedForEmail') {
                const emailError = validateEmail(value);
                if (emailError) {
                    toast.error(emailError);
                    return;
                }
            }
            else if (field === 'preparedForPhone') {
                const phoneError = validatePhone(value);
                if (phoneError) {
                    toast.error(phoneError);
                    return;
                }
            }
            onUpdatePrimaryContact(field, value);
        }
    };
    return (React.createElement(React.Fragment, null,
        React.createElement(DetailSection, { title: translate('consumer_information', { namespace }), action: translate('add_contact', { namespace }), onActionClick: handleAddContact },
            React.createElement("div", { className: "proposal-contacts-section", role: "region", "aria-label": "contacts-section" },
                React.createElement("div", { className: "contact-section" },
                    React.createElement(KeyValueList, { items: primaryContactItems, onEdit: handleContactEdit })),
                contacts.length > 0 && (React.createElement("div", { className: "additional-contacts", role: "list", "aria-label": "Additional contacts" }, contacts.map((contact) => (React.createElement("div", { key: contact.id, className: "contact-card additional-contact", role: "listitem" },
                    React.createElement(KeyValueList, { items: formatContactKeyValueItems(contact, contact.id, true), onEdit: handleContactEdit }),
                    React.createElement("div", { className: "contact-header" },
                        React.createElement(LoadableButton, { variant: "secondary", loading: isDeleting, onClick: () => handleDeleteContact(contact.id), className: "delete-contact-btn", "aria-label": `Delete contact ${contact.name}` }, translate('delete', { namespace })))))))),
                isAddingContact && (React.createElement("div", { className: "contact-card new-contact", role: "group", "aria-label": "New Contact" },
                    React.createElement(KeyValueList, { items: formatContactKeyValueItems(newContact, undefined, true), onEdit: handleContactEdit }),
                    React.createElement("div", { className: "contact-header" },
                        React.createElement("div", { className: "contact-actions" },
                            React.createElement(LoadableButton, { variant: "primary", loading: isCreating, onClick: handleSaveNewContact, disabled: newContactFormDisabled, className: "save-contact-btn" }, translate('save', { namespace: 'features.commons' })),
                            React.createElement(LoadableButton, { variant: "secondary", loading: false, onClick: handleCancelAdd, className: "cancel-contact-btn" }, translate('cancel', { namespace: 'features.commons' })))))))),
        contactToDelete && (React.createElement(ConfirmDialog, { isOpen: !!contactToDelete, title: translate('confirm_delete_contact_title', { namespace }), message: translate('confirm_delete_contact_message', { namespace }), confirmText: translate('delete', { namespace: 'features.commons' }), cancelText: translate('cancel', { namespace: 'features.commons' }), onConfirm: confirmDelete, onCancel: cancelDelete, variant: "danger" }))));
}
